import {
  RAISED_CUSTOMS_FEE_RUB,
  normalizeTnvedCode,
  parseCustomsFee,
  resolveCustomsZnak,
  type ChestnyZnak,
  type CustomsAnswerInput,
  type CustomsCatalogHintDto,
  type CustomsQueueRow,
} from '@crm/shared'
import { partKey } from '../customsSelection'

/**
 * Форма ручного ввода ответа брокера (Л1): разбор полей, предзаполнение и
 * сборка ответа для `POST /customs/answers`. Разметка — CustomsAnswerModal.
 */

/** Сбор: не известен / 0 ₽ / повышенный (73 860 ₽, сумма редактируется). */
export type FeeChoice = 'UNKNOWN' | 'ZERO' | 'RAISED'

export interface AnswerFormState {
  tnved: string
  duty: string
  fee: FeeChoice
  feeAmount: string
  nonTariffNote: string
  certificateNote: string
  featuresNote: string
  znak: ChestnyZnak
  znakFrom: string
}

/** Число из поля пошлины («8», «7,5», «8 %»); пусто → null; мусор → undefined. */
function dutyNumber(raw: string): number | null | undefined {
  const s = raw.replace(/[\s%]/g, '').replace(',', '.')
  if (s === '') return null
  if (!/^\d+(\.\d+)?$/.test(s)) return undefined
  const n = Number(s)
  return Number.isFinite(n) ? n : undefined
}

/** Пошлина в процентах из поля ввода («8», «7,5», «8 %») → доля; пусто → null; мусор → undefined. */
export function parseDutyPercent(raw: string): number | null | undefined {
  const n = dutyNumber(raw)
  if (n == null) return n
  if (n > 100) return undefined
  return Math.round(n * 10000) / 1_000_000
}

/** Число процентов для поля: 8 → «8», 7.5 → «7,5». */
function percentText(pct: number): string {
  return String(Math.round(pct * 10000) / 10000).replace('.', ',')
}

function dutyToPercentText(rate: number | null | undefined): string {
  if (rate == null) return ''
  return percentText(rate * 100)
}

/**
 * Введено число больше 0 и меньше 1 — похоже на долю из файла брокера
 * («0,08» = 8 %). Возвращает, сколько это процентов, иначе null.
 */
export function dutyFractionPercent(raw: string): number | null {
  const n = dutyNumber(raw)
  if (n == null || n <= 0 || n >= 1) return null
  return Math.round(n * 100 * 10000) / 10000
}

/** Значение поля пошлины после «Это 8 %»: доля × 100. */
export function dutyFractionFix(raw: string): string {
  const pct = dutyFractionPercent(raw)
  return pct == null ? raw : percentText(pct)
}

/** «0,08» для подсказки — как ввели, без пробелов и «%». */
export function dutyInputText(raw: string): string {
  return raw.replace(/[\s%]/g, '').replace('.', ',')
}

function feeChoiceOf(raised: boolean | null | undefined): FeeChoice {
  if (raised == null) return 'UNKNOWN'
  return raised ? 'RAISED' : 'ZERO'
}

const EMPTY_FORM: AnswerFormState = {
  tnved: '',
  duty: '',
  fee: 'UNKNOWN',
  feeAmount: String(RAISED_CUSTOMS_FEE_RUB),
  nonTariffNote: '',
  certificateNote: '',
  featuresNote: '',
  znak: 'UNKNOWN',
  znakFrom: '',
}

/**
 * Строка, из которой предзаполняем форму: одна выбранная — она; несколько
 * одного партномера — с самой свежей проверкой (по дате проверки), а без
 * проверок — первая (подставится память по партномеру). Разные партномера — null.
 */
function pickSourceRow(rows: CustomsQueueRow[]): CustomsQueueRow | null {
  if (rows.length === 0) return null
  if (rows.length === 1) return rows[0]!
  const keys = new Set(rows.map((r) => partKey(r.partNumber)))
  if (keys.size !== 1) return null
  const checked = rows
    .filter((r) => r.check)
    .sort((a, b) => (b.check!.checkedAt ?? '').localeCompare(a.check!.checkedAt ?? ''))
  return checked[0] ?? rows[0]!
}

/**
 * Предзаполнение формы: своя проверка, иначе память по партномеру (только
 * ТН ВЭД и «Честный знак» — доверенные). Знак — по общему правилу
 * resolveCustomsZnak: сохранённое логистом «Неизвестно» память не перебивает.
 * Сбор: `null` в проверке — «Неизвестно».
 */
export function initialAnswerForm(rows: CustomsQueueRow[]): {
  form: AnswerFormState
  archiveDate: string | null
} {
  const src = pickSourceRow(rows)
  if (!src) return { form: EMPTY_FORM, archiveDate: null }
  const check = src.check
  // Один партномер — одна память; берём у любой строки, где она пришла.
  const hint: CustomsCatalogHintDto | null = src.hint ?? rows.find((r) => r.hint)?.hint ?? null
  const znakSrc = resolveCustomsZnak(check, hint)
  const tnvedFromHint = !check?.tnvedCode && !!hint?.tnvedCode
  return {
    form: {
      ...EMPTY_FORM,
      tnved: check?.tnvedCode ?? hint?.tnvedCode ?? '',
      duty: dutyToPercentText(check?.dutyRate),
      fee: feeChoiceOf(check?.customsFeeRaised),
      feeAmount: String(check?.customsFeeAmount || RAISED_CUSTOMS_FEE_RUB),
      nonTariffNote: check?.nonTariffNote ?? '',
      certificateNote: check?.certificateNote ?? '',
      featuresNote: check?.featuresNote ?? '',
      znak: znakSrc?.value ?? 'UNKNOWN',
      znakFrom: znakSrc?.value === 'YES' ? (znakSrc.from ?? '') : '',
    },
    archiveDate: tnvedFromHint || znakSrc?.fromArchive ? (hint?.lastCheckedAt ?? null) : null,
  }
}

/** Форма → ответ для сервера; строка — текст ошибки проверки. */
export function buildCustomsAnswer(form: AnswerFormState): CustomsAnswerInput | string {
  const tnvedRaw = form.tnved.trim()
  const tnvedCode = tnvedRaw ? normalizeTnvedCode(tnvedRaw) : null
  if (tnvedRaw && !tnvedCode) return 'Код ТН ВЭД — 10 цифр'
  const dutyRate = parseDutyPercent(form.duty)
  if (dutyRate === undefined) return 'Пошлина — число процентов от 0 до 100, например 8 или 7,5'
  let customsFeeRaised: boolean | null = null
  let customsFeeAmount: number | null = null
  if (form.fee === 'ZERO') {
    customsFeeRaised = false
    customsFeeAmount = 0
  } else if (form.fee === 'RAISED') {
    customsFeeAmount = parseCustomsFee(form.feeAmount)
    if (customsFeeAmount == null || customsFeeAmount <= 0)
      return 'Укажите сумму повышенного сбора, ₽'
    customsFeeRaised = true
  }
  const text = (s: string) => s.trim() || null
  return {
    tnvedCode,
    dutyRate,
    customsFeeRaised,
    customsFeeAmount,
    nonTariffNote: text(form.nonTariffNote),
    certificateNote: text(form.certificateNote),
    featuresNote: text(form.featuresNote),
    chestnyZnak: form.znak,
    chestnyZnakFrom: form.znak === 'YES' && form.znakFrom ? form.znakFrom : null,
  }
}

/** «У 1 позиции», «у 3 позиций», «у 21 позиции». */
export function genitivePositions(n: number): string {
  return n % 10 === 1 && n % 100 !== 11 ? `${n} позиции` : `${n} позиций`
}
